import {
  Box,
  Flex,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  Badge,
} from "@chakra-ui/react";
import { Column } from "./constants";
import EmptyState from "./EmptyState";
import Pagination from "../FinancialShared/Pagination";

const renderStatus = (status: any) => {
  if (!status && status !== 0) return "-";
  
  // Converter para string e uppercase
  const statusStr = String(status).toUpperCase().trim();
  
  console.log("🔍 FGTS Status recebido:", status, "→ Tipo:", typeof status, "→ Processado:", statusStr);
  
  // Mapeamento de status (incluindo variações numéricas e textuais)
  const statusMap: { [key: string]: { label: string; colorScheme: string } } = {
    // Status em inglês
    PAYED: { label: "PAGO", colorScheme: "green" },
    PAID: { label: "PAGO", colorScheme: "green" },
    PENDING: { label: "PENDENTE", colorScheme: "yellow" },
    CANCELLED: { label: "CANCELADO", colorScheme: "red" },
    REJECTED: { label: "REJEITADO", colorScheme: "red" },
    APPROVED: { label: "APROVADO", colorScheme: "green" },
    
    // Status em português
    PAGO: { label: "PAGO", colorScheme: "green" },
    PENDENTE: { label: "PENDENTE", colorScheme: "yellow" },
    CANCELADO: { label: "CANCELADO", colorScheme: "red" },
    REJEITADO: { label: "REJEITADO", colorScheme: "red" },
    APROVADO: { label: "APROVADO", colorScheme: "green" },
    
    // Status numéricos (caso existam)
    "3": { label: "PAGO", colorScheme: "green" },
    "2": { label: "APROVADO", colorScheme: "green" },
    "1": { label: "PENDENTE", colorScheme: "yellow" },
    "0": { label: "CANCELADO", colorScheme: "red" },
  };
  
  const statusInfo = statusMap[statusStr] || { 
    label: statusStr, 
    colorScheme: "gray" 
  };
  
  console.log("✅ FGTS Status mapeado:", statusInfo);
  
  return (
    <Badge 
      colorScheme={statusInfo.colorScheme}
      fontSize="xs"
      px={3}
      py={1}
      borderRadius="md"
      textTransform="uppercase"
      fontWeight="bold"
    >
      {statusInfo.label}
    </Badge>
  );
};

interface CommissionTableProps {
  backgroundStyle: any;
  columns: Column[];
  data: any[];
  isQuerying: boolean;
  hasQueried: boolean;
  meta: {
    totalPages: number;
    currentPage: number;
    itemsPerPage: number;
  };
  pageSize: number;
  onPageSizeChange: (size: number) => void;
  onPageChange: (page: number) => void;
  onClearFilters: () => void;
  formatCell: (key: string, value: any) => string;
}

export default function CommissionTable({
  backgroundStyle,
  columns,
  data,
  isQuerying,
  hasQueried,
  meta,
  pageSize,
  onPageSizeChange,
  onPageChange,
  onClearFilters,
  formatCell,
}: CommissionTableProps) {
  const totalPages = meta.totalPages || 1;

  if (!hasQueried && !isQuerying) {
    return <EmptyState mode="initial" />;
  }

  if (isQuerying) {
    return (
      <Box
        w="100%"
        maxW="100%"
        minW="0"
        p={{ base: 4, md: 6 }}
        borderWidth="1px"
        borderRadius="xl"
        boxShadow="base"
        bg="white"
      >
        <Flex
          justifyContent="center"
          alignItems="center"
          minH="400px"
        >
          <Spinner size="xl" color="primary" thickness="4px" />
        </Flex>
      </Box>
    );
  }

  if (hasQueried && data.length === 0) {
    return <EmptyState mode="no-results" onClearFilters={onClearFilters} />;
  }

  return (
    <Box
      w="100%"
      maxW="100%"
      minW="0"
      borderWidth="1px"
      borderRadius="xl"
      boxShadow="base"
      bg="white"
    >
      <Box
        overflowX="auto"
        css={{
          "&::-webkit-scrollbar": {
            height: "8px",
          },
          "&::-webkit-scrollbar-track": {
            background: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#CBD5E0",
            borderRadius: "10px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background: "#A0AEC0",
          },
        }}
      >
      <Table
        size="sm"
        w="100%"
        minW="max-content"
        variant="simple"
        style={{ borderCollapse: "collapse" }}
      >
        <Thead
          {...backgroundStyle}
          position="sticky"
          top={0}
          zIndex={2}
        >
          <Tr>
            {columns.map((col, index) => (
              <Th
                key={col.key}
                borderTopLeftRadius={index === 0 ? "xl" : "0"}
                borderTopRightRadius={index === columns.length - 1 ? "xl" : "0"}
                color="white"
                textAlign="center"
                fontSize="xs"
                fontWeight="bold"
                py={5}
                px={4}
                whiteSpace="nowrap"
                isNumeric={col.isNumeric}
                textTransform="uppercase"
                letterSpacing="wider"
              >
                {col.label}
              </Th>
            ))}
          </Tr>
        </Thead>
        <Tbody>
          {data.map((row, i) => (
            <Tr
              key={i}
              _hover={{ bg: "gray.50" }}
              transition="background 0.2s"
              borderBottom="1px solid"
              borderColor="gray.100"
            >
              {columns.map((col) => (
                  <Td
                    key={col.key}
                    textAlign="center"
                    fontSize="sm"
                    py={5}
                    px={4}
                    whiteSpace="nowrap"
                    isNumeric={col.isNumeric}
                    color={["csll", "irrf", "pis", "cofins"].includes(col.key) && row[col.key] !== 0 ? "red.500" : "gray.700"}
                  >
                    {col.key === "status" ? renderStatus(row[col.key]) : formatCell(col.key, row[col.key])}
                  </Td>
                ))}
              </Tr>
            ))}
        </Tbody>
      </Table>

      </Box>

      {hasQueried && data.length > 0 && (
        <Pagination
          currentPage={meta.currentPage}
          totalPages={totalPages}
          pageSize={pageSize}
          isQuerying={isQuerying}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
        />
      )}
    </Box>
  );
}
